import { Link } from "react-router-dom";
import {
  ArrowRight,
  Mail,
  MapPin,
  ShoppingBag,
  Store,
  Truck,
  Hand,
} from "lucide-react";

// The four roles, colored with the real brand palette (no legacy blue/yellow).
const roleChips = [
  { label: "Buyer", icon: ShoppingBag, dot: "bg-kwk-orange" },
  { label: "Seller", icon: Store, dot: "bg-kwk-sky" },
  { label: "Driver", icon: Truck, dot: "bg-kwk-green" },
  { label: "Picker", icon: Hand, dot: "bg-kwk-coral" },
];

// Every entry resolves to a real destination — an existing route, an in-page
// section anchor, the lead funnel (/#join), or a mailto. No dead ends.
const footerLinks = {
  "For Drivers": [
    { label: "Why Drive with KwK", to: "/drivers" },
    { label: "Fair Pay", to: "/drivers#fair-pay" },
    { label: "Requirements", to: "/drivers#requirements" },
    { label: "Apply to Drive", to: "/#join", state: { role: "driver" } },
  ],
  "For Sellers": [
    { label: "Game Advertising", to: "/game-promotions" },
    { label: "Grow Your Business", to: "/sellers" },
    { label: "For Propane Retailers", to: "/retailers" },
    { label: "Become a Partner", to: "/#join", state: { role: "seller" } },
  ],
  Services: [
    { label: "KwK Play", to: "/play" },
    { label: "KwK Tube", to: "/tube" },
    { label: "KwK Tunes", to: "/tunes" },
    { label: "Propane Exchange", to: "/propane-exchange" },
    { label: "Points & Rewards", to: "/rewards" },
    { label: "For Pickers", to: "/pickers" },
    { label: "For Buyers", to: "/buyers" },
    { label: "How the Loop Works", to: "/#ecosystem" },
    { label: "The Launcher", to: "/#launcher" },
  ],
  Company: [
    { label: "Service area", to: "/#service-area" },
    { label: "Ask about availability", to: "/#join", state: { role: "buyer" } },
    { label: "Contact", to: "/#join" },
  ],
};

export default function Footer() {
  return (
    <footer className="relative overflow-hidden bg-kwk-ink text-white">
      {/* warm launcher-wallpaper glow so the footer reads as part of the same OS */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 top-0 h-72 w-72 rounded-full bg-kwk-orange/25 blur-3xl" />
        <div className="absolute -right-24 top-10 h-64 w-64 rounded-full bg-kwk-coral/20 blur-3xl" />
      </div>

      {/* Closing CTA band */}
      <div className="relative border-b border-white/10">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 py-14 sm:px-6 lg:grid-cols-[1.25fr_1fr] lg:px-8">
          <div>
            <p className="mb-3 text-xs font-black uppercase tracking-[0.3em] text-kwk-amber">
              Launching neighborhood by neighborhood
            </p>
            <h2 className="max-w-xl text-3xl font-black leading-tight sm:text-4xl">
              Your next order. Your next opportunity. Your neighborhood.
            </h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {roleChips.map((r) => {
                const Icon = r.icon;
                return (
                  <Link
                    key={r.label}
                    to={`/${r.label.toLowerCase()}s`}
                    className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 text-sm font-bold text-white ring-1 ring-white/15"
                  >
                    <span className={`h-2 w-2 rounded-full ${r.dot}`} />
                    <Icon className="h-4 w-4" />
                    {r.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row lg:justify-end">
            <Link
              to="/buyers"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-kwk-orange px-6 py-4 font-black text-white shadow-soft transition hover:bg-kwk-orange-dark"
            >
              Discover your KwK
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/sellers"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 px-6 py-4 font-bold text-white ring-1 ring-white/25 backdrop-blur transition hover:bg-white/20"
            >
              Become a partner
            </Link>
          </div>
        </div>
      </div>

      {/* Link grid */}
      <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4 lg:grid-cols-5 lg:gap-12">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <Link
              to="/"
              className="mb-4 inline-flex items-center rounded-2xl bg-white px-2.5 py-1.5 shadow-soft ring-1 ring-black/5"
            >
              <img src="/kwk-logo.png" alt="KwK Net" className="h-9 w-auto" />
            </Link>
            <p className="max-w-xs text-sm leading-6 text-white/65">
              Local shopping, independent work, growing businesses and more to
              discover with Play, Tube and Tunes. Buyers, drivers, pickers and
              sellers—connected through KwK.
            </p>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h3 className="mb-4 text-sm font-black uppercase tracking-[0.18em] text-white/80">
                {title}
              </h3>
              <ul className="space-y-2.5">
                {links.map((link) => (
                  <li key={`${title}-${link.label}`}>
                    {link.href ? (
                      <a
                        href={link.href}
                        className="text-sm text-white/65 transition-colors hover:text-kwk-amber"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.to}
                        state={link.state}
                        className="text-sm text-white/65 transition-colors hover:text-kwk-amber"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Contact + legal */}
        <div className="mt-12 flex flex-col gap-6 border-t border-white/10 pt-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-white/65">
            <Link
              to="/#join"
              className="flex items-center gap-2 transition-colors hover:text-kwk-amber"
            >
              <Mail className="h-4 w-4" />
              Get in touch
            </Link>
            <Link
              to="/#service-area"
              className="flex items-center gap-2 transition-colors hover:text-kwk-amber"
            >
              <MapPin className="h-4 w-4" />
              Service area
            </Link>
          </div>
          <p className="text-sm text-white/45">
            © {new Date().getFullYear()} KwK Tech, Inc. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
